import { useEffect, useRef, useState, useCallback } from "react";

// Browser-native speech recognition (Chrome/Edge/Safari). No API key needed.
// Auto-restarts when the browser stops on a pause of silence, so dictation
// keeps going until the user explicitly stops.
export function useSpeechToText({ onResult, lang = "en-US" } = {}) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(false);
  const recognitionRef = useRef(null);
  const onResultRef = useRef(onResult);
  const keepAliveRef = useRef(false); // true while the user wants to keep dictating

  useEffect(() => { onResultRef.current = onResult; }, [onResult]);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    setSupported(true);
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = lang;
    rec.onresult = (e) => {
      let finalText = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalText += e.results[i][0].transcript;
      }
      if (finalText && onResultRef.current) onResultRef.current(finalText.trim());
    };
    // The Web Speech API auto-stops after a short silence. If the user still
    // wants to dictate, restart it so recording doesn't cut off. Browsers (esp.
    // Chrome) also force-stop after ~60s — this restart makes it continuous
    // until the user taps stop, like Word/Docs dictation.
    const restart = () => {
      if (!keepAliveRef.current) { setListening(false); return; }
      // A short delay avoids "recognition has already started" races on restart.
      setTimeout(() => {
        if (!keepAliveRef.current) return;
        try { rec.start(); } catch { setTimeout(() => { try { rec.start(); } catch {} }, 400); }
      }, 250);
    };
    rec.onend = restart;
    rec.onerror = (e) => {
      // Only a real permission/device block should end dictation. Everything
      // else (no-speech, network blips, aborts) is transient — keep going.
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        keepAliveRef.current = false;
        setListening(false);
      }
      // otherwise let onend fire and restart automatically
    };
    recognitionRef.current = rec;
    return () => { keepAliveRef.current = false; try { rec.stop(); } catch {} };
  }, [lang]);

  const start = useCallback(() => {
    if (!recognitionRef.current) return;
    keepAliveRef.current = true;
    try { recognitionRef.current.start(); setListening(true); } catch {}
  }, []);

  const stop = useCallback(() => {
    keepAliveRef.current = false;
    if (!recognitionRef.current) return;
    try { recognitionRef.current.stop(); } catch {}
    setListening(false);
  }, []);

  const toggle = useCallback(() => (listening ? stop() : start()), [listening, start, stop]);

  return { listening, supported, start, stop, toggle };
}
